import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import ArticleIcon from '@mui/icons-material/Article';
import Layout from '../components/Layout';
import ProjectService from '../services/ProjectService';
import PortalAuthService from '../services/PortalAuthService';
import { useToast } from '../contexts/ToastContext';
import INVOICE_STATUS_OPTIONS_COLORS from './enums/invoiceStatusOptionsColors';
import { INVOICE_STATUS } from './enums/invoiceStatusOptionsEnum';

const fmt = (dateStr) =>
  dateStr
    ? new Date(dateStr).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';

const fmtCHF = (v) =>
  v != null ? `CHF ${parseFloat(v).toFixed(2)}` : '—';

const statusColor = (status) =>
  INVOICE_STATUS_OPTIONS_COLORS.find(o => o.label === status)?.color || '#64748B';

const SideLabel = ({ children }) => (
  <Typography sx={{ fontSize: 11, color: '#94A3B8', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, mb: 0.4 }}>
    {children}
  </Typography>
);

const SideValue = ({ children, sx }) => (
  <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#1E293B', ...sx }}>
    {children}
  </Typography>
);

const SectionHeader = ({ title, action }) => (
  <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>
      {title}
    </Typography>
    {action}
  </Box>
);

const TERMINAL_STATUSES = [INVOICE_STATUS.PAID, INVOICE_STATUS.CANCELLED, INVOICE_STATUS.WRITTEN_OFF];
const OPEN_STATUSES = [INVOICE_STATUS.ISSUED, INVOICE_STATUS.SENT, INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.OVERDUE];

const PAYMENT_METHODS = ['BANK_TRANSFER', 'CASH', 'CARD', 'OTHER'];

function RecordPaymentDialog({ open, invoiceTotal, paidSoFar, creditedSoFar, onClose, onSave }) {
  const remaining = Math.round(Math.max(0, (parseFloat(invoiceTotal) || 0) - (parseFloat(paidSoFar) || 0) - (parseFloat(creditedSoFar) || 0)) * 100) / 100;
  const [amount, setAmount] = useState(remaining.toFixed(2));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [method, setMethod] = useState('BANK_TRANSFER');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(remaining.toFixed(2));
      setDate(new Date().toISOString().slice(0, 10));
      setMethod('BANK_TRANSFER');
      setReference('');
      setNotes('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const parsedAmount = parseFloat(amount) || 0;
  const amountExceedsRemaining = parsedAmount > remaining;

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ amount: parsedAmount, paymentDate: date, method, reference, notes });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Record Payment</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          label="Amount (CHF)" type="number" value={amount}
          onChange={e => setAmount(e.target.value)}
          inputProps={{ min: 0.01, step: 0.01 }}
          error={amountExceedsRemaining}
          helperText={amountExceedsRemaining ? `Cannot exceed remaining balance of CHF ${remaining.toFixed(2)}` : ''}
          size="small" fullWidth
        />
        <TextField
          label="Payment Date" type="date" value={date}
          onChange={e => setDate(e.target.value)}
          size="small" fullWidth InputLabelProps={{ shrink: true }}
        />
        <FormControl size="small" fullWidth>
          <InputLabel>Method</InputLabel>
          <Select value={method} onChange={e => setMethod(e.target.value)} label="Method">
            {PAYMENT_METHODS.map(m => (
              <MenuItem key={m} value={m}>{m.replace('_', ' ')}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Reference (optional)" value={reference}
          onChange={e => setReference(e.target.value)}
          size="small" fullWidth
        />
        <TextField
          label="Notes (optional)" value={notes}
          onChange={e => setNotes(e.target.value)}
          size="small" fullWidth multiline minRows={2}
        />
        {remaining > 0 && (
          <Typography sx={{ fontSize: 12, color: '#64748B' }}>
            Remaining after payment: CHF {Math.max(0, remaining - (parseFloat(amount) || 0)).toFixed(2)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
        <Button
          variant="contained" onClick={handleSave} disabled={saving || !amount || parsedAmount <= 0 || amountExceedsRemaining}
          sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#356480' } }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreateCreditNoteDialog({ open, invoiceTotal, onClose, onSave }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    if (open) { setAmount(''); setReason(''); setSaveError(null); }
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      await onSave({ amount: parseFloat(amount), reason });
      onClose();
    } catch (err) {
      setSaveError(err?.response?.data?.message || 'Failed to create credit note');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Create Credit Note</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {saveError && <Alert severity="error" sx={{ fontSize: 13 }}>{saveError}</Alert>}
        <TextField
          label="Amount (CHF)" type="number" value={amount}
          onChange={e => setAmount(e.target.value)}
          inputProps={{ min: 0.01, step: 0.01, max: parseFloat(invoiceTotal) || undefined }}
          size="small" fullWidth helperText={`Max: CHF ${parseFloat(invoiceTotal || 0).toFixed(2)}`}
        />
        <TextField
          label="Reason" value={reason} onChange={e => setReason(e.target.value)}
          size="small" fullWidth multiline minRows={2} required
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || !amount || parseFloat(amount) <= 0 || !reason.trim()}
          sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#356480' } }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CancelDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(reason); onClose(); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1, color: '#E07A5F' }}>Cancel Invoice</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
          This cannot be undone. The invoice will be marked as cancelled and retained for legal compliance.
        </Alert>
        <TextField
          label="Cancellation reason" value={reason} onChange={e => setReason(e.target.value)}
          size="small" fullWidth multiline minRows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Dismiss</Button>
        <Button
          variant="contained" onClick={handleConfirm} disabled={saving}
          sx={{ textTransform: 'none', bgcolor: '#E07A5F', '&:hover': { bgcolor: '#c4634c' } }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Cancel Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function WriteOffDialog({ open, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(''); }, [open]);

  const handleConfirm = async () => {
    setSaving(true);
    try { await onConfirm(reason); onClose(); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>Write Off Invoice</DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
          Writing off an invoice marks it as uncollectable. The invoice is retained for accounting purposes.
        </Alert>
        <TextField
          label="Reason" value={reason} onChange={e => setReason(e.target.value)}
          size="small" fullWidth multiline minRows={2}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#64748B' }}>Dismiss</Button>
        <Button
          variant="contained" onClick={handleConfirm} disabled={saving}
          sx={{ textTransform: 'none', bgcolor: '#64748B', '&:hover': { bgcolor: '#475569' } }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Write Off'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function InvoiceInfo() {
  const { id: invoiceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();

  const stateInvoice = location.state?.invoice || null;
  const projectId = location.state?.projectId || null;

  const [invoice, setInvoice] = useState(stateInvoice);
  const [loading, setLoading] = useState(!stateInvoice);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [creditNotes, setCreditNotes] = useState([]);
  const [creditNotesLoading, setCreditNotesLoading] = useState(true);

  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [createCreditNoteOpen, setCreateCreditNoteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [cnPdfUrl, setCnPdfUrl] = useState(null);
  const [cnPdfOpen, setCnPdfOpen] = useState(false);
  const [cnPdfLoading, setCnPdfLoading] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerPortalActive, setCustomerPortalActive] = useState(false);

  const reloadInvoice = useCallback(() => {
    ProjectService.getInvoice(invoiceId)
      .then(data => setInvoice(data))
      .catch(console.error);
  }, [invoiceId]);

  const reloadPayments = useCallback(() => {
    setPaymentsLoading(true);
    ProjectService.getInvoicePayments(invoiceId)
      .then(data => setPayments(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setPaymentsLoading(false));
  }, [invoiceId]);

  const reloadCreditNotes = useCallback(() => {
    setCreditNotesLoading(true);
    ProjectService.getInvoiceCreditNotes(invoiceId)
      .then(data => setCreditNotes(Array.isArray(data) ? data : []))
      .catch(console.error)
      .finally(() => setCreditNotesLoading(false));
  }, [invoiceId]);

  useEffect(() => {
    if (!stateInvoice) {
      ProjectService.getInvoice(invoiceId)
        .then(data => setInvoice(data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      // stateInvoice is the list row object — it lacks nested project.customer needed for
      // portal status check, so always fetch the full entity in the background.
      ProjectService.getInvoice(invoiceId)
        .then(data => setInvoice(data))
        .catch(console.error);
    }
    reloadPayments();
    reloadCreditNotes();
  }, [invoiceId, stateInvoice, reloadPayments, reloadCreditNotes]);

  useEffect(() => {
    const customerId = invoice?.project?.customer?.id;
    if (!customerId) return;
    PortalAuthService.getPortalStatus(customerId)
      .then(data => setCustomerPortalActive(data?.portalStatus === 'ACTIVE'))
      .catch(() => setCustomerPortalActive(false));
  }, [invoice?.project?.customer?.id]);

  useEffect(() => {
    let url;
    setPdfLoading(true);
    ProjectService.downloadPdfInvoice(invoiceId)
      .then(data => {
        url = URL.createObjectURL(new Blob([data.data], { type: 'application/pdf' }));
        setPdfUrl(url);
      })
      .catch(console.error)
      .finally(() => setPdfLoading(false));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [invoiceId]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const data = await ProjectService.downloadPdfInvoice(invoiceId);
      const cd = data.headers['content-disposition'] || '';
      const match = cd.match(/filename\*?=(?:UTF-8'')?"?([^\";]+)/i);
      const name = match ? decodeURIComponent(match[1]) : `invoice-${invoiceId}.pdf`;
      const blobUrl = URL.createObjectURL(new Blob([data.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch { console.error('Failed to download invoice'); }
    finally { setDownloading(false); }
  };

  const handleAction = async (action) => {
    setActionLoading(true);
    setError(null);
    try {
      await action();
      reloadInvoice();
    } catch (e) {
      setError(e?.response?.data?.message || 'Action failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkSent = () => handleAction(() => ProjectService.markInvoiceAsSent(invoiceId));
  const handleIssue = () => handleAction(() => ProjectService.issueInvoice(invoiceId));

  const handlePublishToPortal = async () => {
    try {
      const updated = await ProjectService.publishInvoiceToPortal(invoiceId);
      setInvoice(prev => ({ ...prev, publishedToPortal: updated.publishedToPortal, publishedToPortalAt: updated.publishedToPortalAt, status: updated.status, sentAt: updated.sentAt }));
      showToast('Invoice sent to portal', 'success');
    } catch {
      showToast('Failed to send invoice to portal', 'error');
    }
  };

  const handleUnpublishFromPortal = async () => {
    try {
      const updated = await ProjectService.unpublishInvoiceFromPortal(invoiceId);
      setInvoice(prev => ({ ...prev, publishedToPortal: updated.publishedToPortal, publishedToPortalAt: null }));
      showToast('Invoice removed from portal', 'info');
    } catch {
      showToast('Failed to remove invoice from portal', 'error');
    }
  };

  const handleRecordPayment = async (data) => {
    await ProjectService.recordInvoicePayment(invoiceId, data);
    reloadInvoice();
    reloadPayments();
  };

  const handleDeletePayment = async (paymentId) => {
    await ProjectService.deleteInvoicePayment(invoiceId, paymentId);
    reloadInvoice();
    reloadPayments();
  };

  const handleCreateCreditNote = async (data) => {
    await ProjectService.createCreditNote(invoiceId, data);
    reloadCreditNotes();
    reloadInvoice();
  };

  const handleIssueCreditNote = async (creditNoteId) => {
    await ProjectService.issueCreditNote(creditNoteId);
    reloadCreditNotes();
  };

  const handleViewCreditNotePdf = async (creditNoteId) => {
    setCnPdfLoading(true);
    setCnPdfOpen(true);
    try {
      const res = await ProjectService.downloadCreditNotePdf(creditNoteId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (cnPdfUrl) URL.revokeObjectURL(cnPdfUrl);
      setCnPdfUrl(url);
    } catch {
      showToast('Failed to load credit note PDF', 'error');
      setCnPdfOpen(false);
    } finally {
      setCnPdfLoading(false);
    }
  };

  const handleCancel = async (reason) => {
    await handleAction(() => ProjectService.cancelInvoice(invoiceId, { cancellationReason: reason }));
  };

  const handleWriteOff = async (reason) => {
    await handleAction(() => ProjectService.writeOffInvoice(invoiceId, { reason }));
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress sx={{ color: '#427898' }} />
        </Box>
      </Layout>
    );
  }

  const status = invoice?.status || '';
  const invColor = statusColor(status);
  const isTerminal = TERMINAL_STATUSES.includes(status);
  const isOpen = OPEN_STATUSES.includes(status);
  const isDraft = status === INVOICE_STATUS.DRAFT;
  const isCancelled = status === INVOICE_STATUS.CANCELLED;
  const isWrittenOff = status === INVOICE_STATUS.WRITTEN_OFF;

  const totalPrice = parseFloat(invoice?.totalPrice || 0);
  const paidAmount = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
  const issuedCreditAmount = creditNotes.filter(cn => cn.status === 'ISSUED').reduce((sum, cn) => sum + parseFloat(cn.amount || 0), 0);
  const remainingAmount = Math.max(0, totalPrice - paidAmount - issuedCreditAmount);

  const discountDisplay = invoice?.rabattType === 'PERCENTAGE'
    ? `-${invoice.rabattValue}%`
    : invoice?.rabattType === 'AMOUNT'
      ? `-${invoice.rabattValue} CHF`
      : null;

  const canRecordPayment = isOpen && !isTerminal;
  const canCreateCreditNote = [INVOICE_STATUS.PAID, INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.ISSUED, INVOICE_STATUS.SENT].includes(status);
  const canCancel = isDraft || [INVOICE_STATUS.ISSUED, INVOICE_STATUS.SENT, INVOICE_STATUS.OVERDUE].includes(status);
  const canWriteOff = isOpen && !isWrittenOff;

  return (
    <Layout fullBleed>
      <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh' }}>

        {/* ── Sticky header ── */}
        <Box sx={{
          bgcolor: '#fff', borderBottom: '1px solid #EEF2F6',
          px: 3, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
          position: 'sticky', top: 0, zIndex: 10,
        }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
            onClick={() => projectId ? navigate(`/project-info/${projectId}`) : navigate(-1)}
            sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
          >
            Project
          </Button>

          <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />

          <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>
            {invoice?.invoiceNumber || `Invoice #${invoiceId}`}
          </Typography>

          <Chip
            label={status || '—'}
            size="small"
            sx={{ bgcolor: invColor + '18', color: invColor, fontWeight: 600, fontSize: 11, border: `1px solid ${invColor}30`, height: 22 }}
          />

          <Box sx={{ flex: 1 }} />

          <Stack direction="row" spacing={0.5} alignItems="center">
            {isDraft && (
              <Button
                size="small" variant="contained" onClick={handleIssue} disabled={actionLoading}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, bgcolor: '#427898', '&:hover': { bgcolor: '#356480' } }}
              >
                Issue Invoice
              </Button>
            )}
            {status === INVOICE_STATUS.ISSUED && (
              <Button
                size="small" variant="outlined" onClick={handleMarkSent} disabled={actionLoading}
                sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#6366F1', color: '#6366F1' }}
              >
                Mark as Sent
              </Button>
            )}
            {customerPortalActive && !isCancelled && (
              invoice?.publishedToPortal ? (
                <Button
                  size="small" variant="outlined" onClick={handleUnpublishFromPortal}
                  sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#1565C0', color: '#1565C0' }}
                >
                  Unpublish from Portal
                </Button>
              ) : (
                <Button
                  size="small" variant="outlined" onClick={handlePublishToPortal} disabled={isDraft}
                  sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#1565C0', color: '#1565C0' }}
                >
                  Send to Portal
                </Button>
              )
            )}
            {isOpen && (
              <Tooltip title="Send by email">
                <span>
                  <IconButton size="small"
                    sx={{ color: '#427898', '&:hover': { bgcolor: '#EFF6FF' } }}>
                    <EmailIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title="Download PDF">
              <span>
                <IconButton size="small" onClick={handleDownload} disabled={downloading}
                  sx={{ color: '#64748B', '&:hover': { bgcolor: '#F1F5F9' } }}>
                  {downloading
                    ? <CircularProgress size={16} sx={{ color: '#427898' }} />
                    : <DownloadIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 3, mt: 2, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}

        {/* ── Body ── */}
        <Box sx={{
          display: 'flex', gap: 3, p: { xs: 2, md: 3 },
          maxWidth: 1280, mx: 'auto', alignItems: 'flex-start',
          flexDirection: { xs: 'column', md: 'row' },
        }}>

          {/* ── Main column ── */}
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

            {/* Status / overview card */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader title="Details" />
              <Box sx={{ px: 3, py: 1 }}>
                {[
                  ['Invoice Number', invoice?.invoiceNumber],
                  ['Mode', invoice?.mode],
                  ['Purchase Order', invoice?.purchaseOrderNumber],
                  ['Created', fmt(invoice?.createdAt)],
                  ['Due Date', fmt(invoice?.dueDate)],
                  ['Sent', invoice?.sentAt ? fmt(invoice.sentAt) : null],
                  ['Written Off', invoice?.writtenOffAt ? fmt(invoice.writtenOffAt) : null],
                ].filter(([, v]) => v != null).map(([label, value]) => (
                  <Box key={label} sx={{ py: 1.25, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 500, flexShrink: 0 }}>{label}</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', textAlign: 'right' }}>{value}</Typography>
                  </Box>
                ))}
                {discountDisplay && (
                  <Box sx={{ py: 1.25, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Discount</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#EF4444' }}>{discountDisplay}</Typography>
                  </Box>
                )}
                {invoice?.publishedToPortal && (
                  <Box sx={{ py: 1.25, borderBottom: '1px solid #F1F5F9', display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>Portal</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1565C0' }}>Sent {fmt(invoice.publishedToPortalAt)}</Typography>
                  </Box>
                )}
                {isCancelled && invoice?.cancellationReason && (
                  <Box sx={{ mt: 1.5, mb: 1, p: 1.5, bgcolor: '#FEF2F2', borderRadius: 1.5, border: '1px solid #FECACA' }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#EF4444', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      Cancellation Reason
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#7F1D1D' }}>{invoice.cancellationReason}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* ── Payments card ── */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader
                title="Payments"
                action={canRecordPayment && (
                  <Button
                    size="small" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => setRecordPaymentOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#427898', '&:hover': { bgcolor: '#EFF6FF' } }}
                  >
                    Record Payment
                  </Button>
                )}
              />
              <Box sx={{ px: 3, py: paymentsLoading || payments.length === 0 ? 3 : 0 }}>
                {paymentsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={20} sx={{ color: '#427898' }} />
                  </Box>
                ) : payments.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>No payments recorded</Typography>
                ) : (
                  payments.map((p) => (
                    <Box key={p.id} sx={{ py: 1.5, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                          {fmtCHF(p.amount)}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.3 }}>
                          {fmt(p.paymentDate)} · {p.method?.replace('_', ' ')}
                          {p.reference ? ` · Ref: ${p.reference}` : ''}
                        </Typography>
                        {p.notes && (
                          <Typography sx={{ fontSize: 11, color: '#64748B', mt: 0.3, fontStyle: 'italic' }}>{p.notes}</Typography>
                        )}
                      </Box>
                      {canRecordPayment && (
                        <Tooltip title="Delete payment">
                          <IconButton size="small" onClick={() => handleDeletePayment(p.id)}
                            sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  ))
                )}
                {payments.length > 0 && (
                  <Box sx={{ py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Total Paid</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#7DCFB6' }}>{fmtCHF(paidAmount)}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* ── Credit Notes card ── */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader
                title="Credit Notes"
                action={canCreateCreditNote && (
                  <Button
                    size="small" startIcon={<AddIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={() => setCreateCreditNoteOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#427898', '&:hover': { bgcolor: '#EFF6FF' } }}
                  >
                    Create Credit Note
                  </Button>
                )}
              />
              <Box sx={{ px: 3, py: creditNotesLoading || creditNotes.length === 0 ? 3 : 0 }}>
                {creditNotesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={20} sx={{ color: '#427898' }} />
                  </Box>
                ) : creditNotes.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: '#94A3B8', textAlign: 'center' }}>No credit notes</Typography>
                ) : (
                  creditNotes.map((cn) => (
                    <Box key={cn.id} sx={{ py: 1.5, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                            {cn.displayName} — {fmtCHF(cn.amount)}
                          </Typography>
                          <Chip
                            label={cn.status} size="small"
                            sx={{
                              height: 18, fontSize: 10, fontWeight: 600,
                              bgcolor: cn.status === 'ISSUED' ? '#7DCFB618' : '#94A3B818',
                              color: cn.status === 'ISSUED' ? '#7DCFB6' : '#94A3B8',
                              border: `1px solid ${cn.status === 'ISSUED' ? '#7DCFB630' : '#94A3B830'}`,
                            }}
                          />
                        </Stack>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.3 }}>
                          {fmt(cn.issueDate)}{cn.reason ? ` · ${cn.reason}` : ''}
                        </Typography>
                      </Box>
                      {cn.status === 'ISSUED' && (
                        <Tooltip title="View credit note PDF">
                          <IconButton size="small" onClick={() => handleViewCreditNotePdf(cn.id)}
                            sx={{ color: '#94A3B8', '&:hover': { color: '#427898', bgcolor: '#EFF6FF' } }}>
                            <ArticleIcon sx={{ fontSize: 17 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {cn.status === 'DRAFT' && (
                        <Button
                          size="small" variant="outlined" onClick={() => handleIssueCreditNote(cn.id)}
                          sx={{ textTransform: 'none', fontSize: 11, fontWeight: 600, borderColor: '#7DCFB6', color: '#7DCFB6', whiteSpace: 'nowrap' }}
                        >
                          Issue
                        </Button>
                      )}
                    </Box>
                  ))
                )}
              </Box>
            </Paper>

            {/* ── PDF document card ── */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', overflow: 'hidden' }}>
              <SectionHeader
                title="Document"
                action={
                  <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: '14px !important' }} />}
                    onClick={handleDownload} disabled={downloading}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12, color: '#427898', px: 1.5, '&:hover': { bgcolor: '#EFF6FF' } }}>
                    Download
                  </Button>
                }
              />
              <Box sx={{ height: 860, position: 'relative', bgcolor: '#F8FAFC' }}>
                {pdfLoading ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                    <CircularProgress size={28} sx={{ color: '#427898' }} />
                    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Loading document…</Typography>
                  </Box>
                ) : pdfUrl ? (
                  <iframe
                    src={pdfUrl}
                    title="Invoice PDF"
                    style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Document could not be loaded.</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* ── Sidebar ── */}
          <Box sx={{ width: { md: 288 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2, position: { md: 'sticky' }, top: { md: 72 } }}>

            {/* Financials card */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                Financials
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <SideLabel>Amount before tax</SideLabel>
                  <SideValue>{fmtCHF(invoice?.amountBeforeTax)}</SideValue>
                </Box>
                <Box>
                  <SideLabel>Tax ({invoice?.taxPercent ?? 0}%)</SideLabel>
                  <SideValue>{fmtCHF(invoice?.taxAmount)}</SideValue>
                </Box>
                {discountDisplay && (
                  <Box>
                    <SideLabel>Discount</SideLabel>
                    <SideValue sx={{ color: '#EF4444' }}>{discountDisplay}</SideValue>
                  </Box>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Box>
                  <SideLabel>Total</SideLabel>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#427898', letterSpacing: -0.5 }}>
                    {fmtCHF(invoice?.totalPrice)}
                  </Typography>
                </Box>
                {(paidAmount > 0 || issuedCreditAmount > 0) && (
                  <>
                    {paidAmount > 0 && (
                      <Box>
                        <SideLabel>Paid</SideLabel>
                        <SideValue sx={{ color: '#7DCFB6' }}>{fmtCHF(paidAmount)}</SideValue>
                      </Box>
                    )}
                    {issuedCreditAmount > 0 && (
                      <Box>
                        <SideLabel>Credited</SideLabel>
                        <SideValue sx={{ color: '#A78BFA' }}>{fmtCHF(issuedCreditAmount)}</SideValue>
                      </Box>
                    )}
                    <Box>
                      <SideLabel>Remaining</SideLabel>
                      <SideValue sx={{ color: remainingAmount > 0 ? '#F59E0B' : '#7DCFB6', fontWeight: 700 }}>
                        {fmtCHF(remainingAmount)}
                      </SideValue>
                    </Box>
                  </>
                )}
              </Box>
            </Paper>

            {/* Dates card */}
            <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                Dates
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <SideLabel>Created</SideLabel>
                  <SideValue>{fmt(invoice?.createdAt)}</SideValue>
                </Box>
                <Box>
                  <SideLabel>Due date</SideLabel>
                  <SideValue sx={
                    invoice?.dueDate && new Date(invoice.dueDate) < new Date() && !TERMINAL_STATUSES.includes(status)
                      ? { color: '#EF4444' }
                      : {}
                  }>
                    {fmt(invoice?.dueDate)}
                  </SideValue>
                </Box>
                {invoice?.sentAt && (
                  <Box>
                    <SideLabel>Sent</SideLabel>
                    <SideValue>{fmt(invoice.sentAt)}</SideValue>
                  </Box>
                )}
                {invoice?.writtenOffAt && (
                  <Box>
                    <SideLabel>Written Off</SideLabel>
                    <SideValue sx={{ color: '#64748B' }}>{fmt(invoice.writtenOffAt)}</SideValue>
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Status Actions card */}
            {!isTerminal && (
              <Paper elevation={0} sx={{ border: '1px solid #EEF2F6', borderRadius: 2, bgcolor: '#fff', p: 2.5 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                  Actions
                </Typography>
                <Stack spacing={1}>
                  {canWriteOff && (
                    <Button
                      fullWidth size="small" variant="outlined"
                      onClick={() => setWriteOffOpen(true)}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#64748B', color: '#64748B', justifyContent: 'flex-start' }}
                    >
                      Write Off
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      fullWidth size="small" variant="outlined"
                      onClick={() => setCancelOpen(true)}
                      sx={{ textTransform: 'none', fontSize: 12, fontWeight: 600, borderColor: '#E07A5F', color: '#E07A5F', justifyContent: 'flex-start' }}
                    >
                      Cancel Invoice
                    </Button>
                  )}
                </Stack>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      <RecordPaymentDialog
        open={recordPaymentOpen}
        invoiceTotal={invoice?.totalPrice}
        paidSoFar={paidAmount}
        creditedSoFar={issuedCreditAmount}
        onClose={() => setRecordPaymentOpen(false)}
        onSave={handleRecordPayment}
      />

      <CreateCreditNoteDialog
        open={createCreditNoteOpen}
        invoiceTotal={invoice?.totalPrice}
        onClose={() => setCreateCreditNoteOpen(false)}
        onSave={handleCreateCreditNote}
      />

      <CancelDialog
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={handleCancel}
      />

      <WriteOffDialog
        open={writeOffOpen}
        onClose={() => setWriteOffOpen(false)}
        onConfirm={handleWriteOff}
      />

      <Dialog
        open={cnPdfOpen}
        onClose={() => { setCnPdfOpen(false); if (cnPdfUrl) { URL.revokeObjectURL(cnPdfUrl); setCnPdfUrl(null); } }}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, height: '85vh' } }}
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Credit Note PDF
          <Button
            size="small"
            onClick={() => { setCnPdfOpen(false); if (cnPdfUrl) { URL.revokeObjectURL(cnPdfUrl); setCnPdfUrl(null); } }}
            sx={{ textTransform: 'none', color: '#64748B', minWidth: 0 }}
          >
            Close
          </Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {cnPdfLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 2 }}>
              <CircularProgress size={28} sx={{ color: '#427898' }} />
              <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Loading…</Typography>
            </Box>
          ) : cnPdfUrl ? (
            <iframe
              src={cnPdfUrl}
              title="Credit Note PDF"
              style={{ width: '100%', flex: 1, border: 'none', display: 'block' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
              <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>Could not load document.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
